import * as React from 'react';
import * as Bar from './Bar';
import * as Food from '../../domain/Food';
import * as d3 from 'd3';
import { roundNumber, frac2pct } from '../../lib/number';

const nutrientColor = {
  vitamin: '#9c9ede',
  mineral: '#cedb9c'
};

const NAME = 0;
const SHORT_NAME = 1;
const VALUE = 2;
const UNIT = 3;
const KIND = 4;
const DV = 5;
const DV_GRAPH = 6;
type NutrientData = [string, string, number, string, string, number, number];

type NutrientDensityProps = {
  foodWithNutrients: Food.FoodWithNutrients,
  grams: number,
  servings: number
};
type NutrientDensityState = {
  display: () => void,
  data: NutrientData[]
};

class NutrientDensity extends React.Component<NutrientDensityProps, NutrientDensityState> {
  constructor(props: NutrientDensityProps) {
    super(props);
    this.state = displayMinVit({...props});
  }

  componentWillReceiveProps(nextProps: NutrientDensityProps): void {
    this.setState(
      displayMinVit({...nextProps})
    );
  }

  componentDidMount(): void {
    const { display } = this.state;
    if (display) { display(); }
  }

  componentDidUpdate(): void {
    const { display } = this.state;
    if (display) { display(); }
  }

  render() {
    const { grams, servings } = this.props;
    const { data } = this.state;
    const barGraphRows = data.map(function(d: NutrientData): JSX.Element {
      return Bar.bar_graph(
          d[NAME],
          valueByGram(servings, grams, d).toString() + ' ' + d[UNIT],
          d[DV_GRAPH] / 2,
          nutrientColor[d[KIND]]);
    });

    return (
      <div id="vit-min">
        <div id="vit-min-canvas" className="canvas" />
        <table className="bar-graph">
          <tbody>
            {barGraphRows}
          </tbody>
        </table>
      </div>
   );
  }
}

export default NutrientDensity;

type DisplayResult = {
  display: () => void
  data: NutrientData[]
};

function valueByGram(servings: number, grams: number, d: NutrientData): number {
  return roundNumber(d[VALUE] * (servings * grams) / Food.GRAMS_GIVEN, 4);
}

/* tslint:disable */
function displayMinVit({grams,  servings, foodWithNutrients} : NutrientDensityProps): DisplayResult {
    const values = foodWithNutrients;
    function translate(x: number, y: number): string {
      return 'translate(' + x + ',' + y + ')';
    }
    function value(d: NutrientData): number {
        return valueByGram(servings, grams, d);
    }
    const width = 590;
    const height = 570;
    const innerRadius = 70;
    const outerRadius = 250;
    const MAX_PERCENT = 200;
    const portion = Math.max(values['Energy'], 1) / Food.DAILY_CALORIES;
    function pcnt_dv(dv_frac: number) {
      return Math.min(MAX_PERCENT, frac2pct(dv_frac, portion));
    }
    function to_d3_data(d: any) {
      return {
        name: d[NAME],
        sname: d[SHORT_NAME],
        value: d[VALUE],
        unit: d[UNIT],
        kind: d[KIND],
        dv: d[DV],
        dv_graph: d[DV_GRAPH]
      };
    }
    const ms: NutrientData[] = [];
    for (const n of Food.MINERALS){
      const [dri, unit] = Food.DRI_JSON[n];
      const val = values[n] || 0;
      const frac = dri ? (val / dri) : 0;
      ms.push(
        [n, Food.ABREV[n], val, unit, 'mineral',  frac, pcnt_dv(frac)]
      );
    }
    const vs: NutrientData[] = [];
    for (const n of Food.VITAMINS){
      const [dri, unit] = Food.DRI_JSON[n];
      const val = values[n] || 0;
      const frac = dri ? (val / dri) : 0;
      ms.push(
        [n, Food.ABREV[n], val, unit, 'vitamin',  frac, pcnt_dv(frac)]
      );
    }
    const data = ms.concat(vs);
    const a = outerRadius - innerRadius;
    function radius(dv: number): number {
      return Math.min(outerRadius, innerRadius + (a * dv / MAX_PERCENT));
    }
    const bigAngle = 2.0 * Math.PI / (data.length + 1);
    const smallAngle = bigAngle / 1.2;
    function title(d: NutrientData): string {
      return "" + d[SHORT_NAME] + " - " + d[NAME] + " - " + (value(d).toString()) + d[UNIT] + " - "
        + roundNumber(frac2pct(d[DV], portion)) + "%";
    }

    function display(){
    const xTranslate = outerRadius + 40;
    const yTranslate = outerRadius + 40;
    const donut = d3.layout.pie();
    const arc: any = d3.svg.arc().innerRadius(innerRadius).outerRadius(outerRadius);
    const dom = d3.select('#vit-min-canvas');
    dom.select('svg').remove();
    const vis = dom.append("svg").data([
      data.map(function(d) { return 1; })
    ]).attr("width", width).attr("height", height);
    vis.append('a').attr('xlink:href', process.env.PUBLIC_URL + '/pages/faq.html#explain-vitamin-mineral-graph').append('text').attr("transform", function(d) {
      return translate(420, 20);
    }).text("About this Graph").style('font', "2.6em sans-serif").style("stroke", "hsl(199, 69%, 37%)").attr("fill", "hsl(199, 69%, 37%)");
    const arcs = vis.selectAll("g.arc").data(donut).enter().append("g").attr("class",
    "arc").attr("transform", translate(xTranslate, yTranslate)).attr("title", function(d: any, i: number) {
      return title(data[i]);
    });
    arcs.append("title").text(function(d: any, i: number): string{
      return title(data[i]);
    });
    arcs.append("path").attr("fill", function(d, i: number): string {
      return nutrientColor[data[i][KIND]];
    })
      .attr("d", arc).style('stroke', "#000")
      .attr('stroke-width', 0.5);
    arcs.append("text").attr("transform", function(d) {
      const t = arc.centroid(d);
      t[0] = t[0] * 1.7;
      t[1] = t[1] * 1.7;
      return "translate(" + t + ")";
    }).attr("text-anchor", "middle").text(function(d, i: number): string {
      return data[i][SHORT_NAME];
    }).style('font', "2.8em sans-serif");
    const arc2: any = d3.svg.arc().startAngle(function(d, i: number): number {
      return d.startAngle + 0.02;
    }).endAngle(function(d: any, i: number) {
      return d.endAngle - 0.02;
    }).innerRadius(innerRadius).outerRadius(function(d, i: number): number {
      return radius(data[i][DV_GRAPH]);
    });
    arcs.append("path").attr("fill", function(d, i) {
      return "#4a5584";
    }).attr("d", arc2);
    const circles = vis.selectAll("circle").data([0.25, 0.50, 0.75, 1]).enter().append('circle');
    circles.attr("r", function(d) {
      return innerRadius + d * (outerRadius - innerRadius);
    }).attr("cx", xTranslate).attr("cy", yTranslate).style("fill", "none").style('stroke', "#777").style('line-width', 1);
    vis.append('text').attr("transform", function(d) {
      return translate(xTranslate, yTranslate - outerRadius - 4);
    }).attr("text-anchor", "middle").text('GREAT').style('font', "2.0em sans-serif");
    vis.append('text').attr("transform", function(d) {
      return translate(xTranslate, +(yTranslate - innerRadius - ((outerRadius - innerRadius) * 0.5) - 4));
    }).attr("text-anchor", "middle").text('GOOD').style('font', "2.0em sans-serif");
    vis.append('a').attr('xlink:href', '/pages/faq#q.-can-you-explain-the-vitamin-mineral-graph').append('text').attr('text-anchor', "middle").attr("transform", function(d) {
      return translate(xTranslate, yTranslate + 25);
    }).text("Nutrient Density").style('font', "1.6rem sans-serif");
    vis.append('text').attr('text-anchor', "middle").attr("transform", function(d) {
      return translate(xTranslate, +(yTranslate + 50));
    }).text("Profile").style('font', "1.6rem sans-serif");
    function translateY(i: number) {
      return 15 + yTranslate - 37 * (i + 1);
    }
    const key = vis.selectAll("rect").data(d3.keys(nutrientColor)).enter();
    key.append('rect').attr("transform", function(d, i) {
      return translate(xTranslate - 30, translateY(i));
    }).attr('width', 60).attr('height', 28).style('fill', function(d) {
      return nutrientColor[d];
    });
    key.append('text').text(function(d) {
      return d;
    }).style('font', "1.5rem sans-serif").attr("transform", function(d, i) {
      return translate(xTranslate - 25, translateY(i) + 20);
    });
    /*
    $('#vit-min svg g').tipsy();
    */
    }

    return { display, data }
};
// tslint:enable



// WEBPACK FOOTER //
// ./src/feature/graphs/NutrientDensity.tsx